import { ItemType } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import CaptiveDialog from 'features/rdc/components/CaptiveDialog/CaptiveDialog';
import useRDCNavigator from 'features/rdc/hooks/useRDCNavigator';
import { RDCSearchParams } from 'features/rdc/RDCSearchParams';
import rdcStrings from 'features/rdc/rdcStrings.en';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

export default function CaptureFrontPrompt() {
  const router = useRouter();
  const { result, fetching } = useGetProfileInfo('cache-first');
  const { session, account, amount, depositFlowId } =
    useLocalSearchParams<RDCSearchParams>();
  const { t: translatedText } = useTranslation('rdc');
  const { captureFrontPrompt } = rdcStrings;
  const { finish } = useRDCNavigator();
  const fullName = (): string => {
    return result?.customerInfo.name?.full
      ? `, ${result?.customerInfo.name?.full}`
      : '';
  };
  const [modalVisible, setModalVisible] = useState(false);

  const { bottom } = useSafeAreaInsets();

  const handleBackButtonAction = () => {
    setModalVisible(true);
  };

  useBackHandler(() => {
    handleBackButtonAction();
  });

  const logRdcCaptureExitedEvent = usePressableAnalytics({
    analyticsId: 'rdcCaptureExited',
    customAttributes: { source_name: 'rdc_front' },
  });

  const handleTakePhotoAction = () => {
    router.replace({
      pathname: './capture',
      params: {
        session,
        account,
        amount,
        mode: 'front',
        depositFlowId,
      },
    });
  };
  if (isWeb) {
    return <Redirect href="/move-money/check-deposit" />;
  }
  return (
    <View style={baseStyles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          gestureEnabled: false,
          title: translatedText('headerTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={handleBackButtonAction}
            />
          ),
        }}
      />
      <ScrollView
        style={baseStyles.scrollView}
        contentContainerStyle={baseStyles.headerContainer}
        testID="capture-front-scroll-view"
      >
        <TitleLgText>
          {translatedText('captureFrontPrompt.title', captureFrontPrompt.title)}
        </TitleLgText>
        <BodyBaseText>
          {translatedText(
            'captureFrontPrompt.subTitle',
            captureFrontPrompt.subTitle,
          )}
        </BodyBaseText>
        <ContentList
          itemType={ItemType.Bullet}
          contentList={[
            <BodyBaseText key="bullet-1">
              {translatedText(
                'captureFrontPrompt.bullets.one',
                captureFrontPrompt.bullets.one,
              ) + fullName()}
            </BodyBaseText>,
            <BodyBaseText key="bullet-2">
              {translatedText(
                'captureFrontPrompt.bullets.two',
                captureFrontPrompt.bullets.two,
              )}
            </BodyBaseText>,
          ]}
        />
      </ScrollView>

      <View
        style={[
          baseStyles.footerContainer,
          { paddingBottom: bottom + theme.spacing.m },
        ]}
      >
        <Button
          label={translatedText(
            'captureFrontPrompt.buttons.takePhoto',
            captureFrontPrompt.buttons.takePhoto,
          )}
          size="LARGE"
          type="PRIMARY"
          onPress={handleTakePhotoAction}
        />

        <Button
          label={translatedText(
            'captureFrontPrompt.buttons.cancel',
            captureFrontPrompt.buttons.cancel,
          )}
          size="LARGE"
          type="SECONDARY"
          onPress={() => setModalVisible(true)}
        />
      </View>

      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={null}
      />

      <CaptiveDialog
        isDialogVisible={modalVisible}
        setModalVisible={setModalVisible}
        handlePrimaryAction={() => {
          logRdcCaptureExitedEvent();
          finish();
        }}
        handleSecondaryAction={(value) => setModalVisible(value)}
      />
    </View>
  );
}

const baseStyles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
  scrollView: {
    flex: 1,
  },
  footerContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  headerContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
});
